export const TAX_STORAGE_KEY = 'alfinder_tax';
export const TAX_TIMESTAMP_KEY = 'alfinder_tax_timestamp';
export const CURRENCY_STORAGE_KEY = 'alfinder_currency';
export const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export const getSallaProductDetails = async (productId: number) => {
  const priceData = await window.salla?.api.product.getDetails(productId);
  return priceData.data.regular_price;
};

export const getZidProductDetails = async (productId: string) => {
  const has_vitrin = window.vitrin == true;
  if (has_vitrin) {
    const response = await window.zid?.products.get(productId);
    return response.price;
  } else {
    const response = await window.zid?.store.product.fetch(productId);
    return response.data.product.price;
  }
};

export const calculateTax = (
  priceWithTaxes: number,
  priceWithoutTaxes: number,
) => {
  // Validate inputs
  if (
    !isFinite(priceWithTaxes) ||
    !isFinite(priceWithoutTaxes) ||
    priceWithoutTaxes === 0 ||
    priceWithTaxes <= 0 ||
    priceWithoutTaxes <= 0
  ) {
    console.warn('Invalid tax calculation inputs:', {
      priceWithTaxes,
      priceWithoutTaxes,
    });
    return NaN;
  }

  const tax = priceWithTaxes / priceWithoutTaxes;

  // Validate result
  if (!isFinite(tax) || tax <= 0) {
    console.warn('Invalid tax calculation result:', tax);
    return NaN;
  }

  return tax;
};

export const getPriceWithTaxes = async (
  storeType: string,
  productId: string | number,
) => {
  switch (storeType) {
    case 'salla':
      return getSallaProductDetails(Number(productId));
    case 'zid':
      return getZidProductDetails(productId.toString());
    default:
      return null;
  }
};

export const getTaxWithExpiry = (): number | null => {
  const storedTax = localStorage.getItem(TAX_STORAGE_KEY);
  const timestamp = localStorage.getItem(TAX_TIMESTAMP_KEY);

  console.log('Stored tax:', storedTax);
  console.log('Stored timestamp:', timestamp);

  if (!storedTax || !timestamp) {
    return null;
  }

  const now = new Date().getTime();
  console.log('Time difference (ms):', now - parseInt(timestamp));
  console.log('One week in ms:', ONE_WEEK_MS);

  if (now - parseInt(timestamp) > ONE_WEEK_MS) {
    // Tax value has expired, clear it
    localStorage.removeItem(TAX_STORAGE_KEY);
    localStorage.removeItem(TAX_TIMESTAMP_KEY);
    return null;
  }

  const parsedTax = parseFloat(storedTax);

  // Check if the parsed tax is NaN or invalid
  if (isNaN(parsedTax) || !isFinite(parsedTax) || parsedTax <= 0) {
    console.log(
      'Stored tax is invalid (NaN, infinite, or non-positive), clearing tax data',
    );
    localStorage.removeItem(TAX_STORAGE_KEY);
    localStorage.removeItem(TAX_TIMESTAMP_KEY);
    return null;
  }

  return parsedTax;
};

export const storeTaxWithExpiry = (tax: number) => {
  // Validate tax before storing
  if (isNaN(tax) || !isFinite(tax) || tax <= 0) {
    console.warn('Attempted to store invalid tax value:', tax);
    return;
  }

  const timestamp = new Date().getTime();
  localStorage.setItem(TAX_STORAGE_KEY, tax.toString());
  localStorage.setItem(TAX_TIMESTAMP_KEY, timestamp.toString());
  // Add logging to verify storage
  console.log('Storing tax:', tax);
  console.log('Storing timestamp:', timestamp);
};

export const handleCurrencyChange = (currentCurrency: string) => {
  const lastCurrency = localStorage.getItem(CURRENCY_STORAGE_KEY);

  console.log('Currency check:', {
    current: currentCurrency,
    last: lastCurrency,
  });

  if (lastCurrency && lastCurrency !== currentCurrency) {
    console.log('Currency changed, clearing tax data');
    localStorage.removeItem(TAX_STORAGE_KEY);
    localStorage.removeItem(TAX_TIMESTAMP_KEY);
  }

  localStorage.setItem(CURRENCY_STORAGE_KEY, currentCurrency);
};

export const getTaxWithRetry = async (
  storeType: string,
  productId: string | number,
  productPrice: number,
  maxRetries: number = 2,
): Promise<number | null> => {
  const currentHost = window.location.hostname.toLowerCase();

  // ✅ Force tax = 1.15 for balsamalula.com
  if (currentHost.includes('balsamalula.com')) {
    const storedTax = getTaxWithExpiry();

    // If tax not found, invalid, or expired — store fixed one
    if (!storedTax || isNaN(storedTax) || storedTax !== 1.15) {
      console.log('Detected balsamalula.com — setting fixed tax = 1.15');
      localStorage.removeItem(TAX_STORAGE_KEY);
      localStorage.removeItem(TAX_TIMESTAMP_KEY);
      storeTaxWithExpiry(1.15);
    } else {
      console.log('balsamalula.com fixed tax already valid');
    }

    return 1.15;
  }

  // ... rest of your existing logic below
  let tax = getTaxWithExpiry();

  if (tax !== null && !isNaN(tax) && isFinite(tax) && tax > 0) {
    return tax;
  }

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(
        `Attempting to calculate tax, attempt ${attempt + 1}/${maxRetries}`,
      );
      const priceWithTaxes = await getPriceWithTaxes(storeType, productId);
      if (priceWithTaxes === null) {
        console.warn(
          `Failed to get price with taxes on attempt ${attempt + 1}`,
        );
        continue;
      }

      tax = calculateTax(priceWithTaxes, productPrice);
      if (!isNaN(tax) && isFinite(tax) && tax > 0) {
        console.log(
          `Successfully calculated tax: ${tax} on attempt ${attempt + 1}`,
        );
        storeTaxWithExpiry(tax);
        return tax;
      } else {
        console.warn(`Invalid tax calculated on attempt ${attempt + 1}:`, tax);
      }
    } catch (error) {
      console.error(`Error calculating tax on attempt ${attempt + 1}:`, error);
    }
  }

  console.error(`Failed to calculate valid tax after ${maxRetries} attempts`);
  return null;
};
